import { useState, useRef } from "react";
import { useCreateNote, useUpdateNote, useDeleteNote } from "../hooks/useGrowsQuery.js";
import { useUploadPhoto, useReplacePhoto } from "../hooks/usePhotosQuery.js";
import useTimezone from "../hooks/useTimezone.js";
import { formatDateTimeWithRelative } from "../utility/datetime.js";
import { prepareForUpload, isTouchDevice } from "../utility/imageUpload.js";
import { Lightbox } from "./PhotoGallery.jsx";

const EVENT_TYPES = [
  { value: "",                label: "Plain note" },
  { value: "misted",          label: "Misted" },
  { value: "fan_adjusted",    label: "Fan adjusted" },
  { value: "harvested",       label: "Harvested" },
  { value: "contam_spotted",  label: "Contamination spotted" },
  { value: "phase_transition",label: "Phase transition" },
  { value: "photo_taken",     label: "Photo taken" },
  { value: "custom",          label: "Custom event" },
];

const EVENT_STYLES = {
  misted:           { label: "Misted",        bg: "bg-blue-100 dark:bg-blue-950",     text: "text-blue-700 dark:text-blue-400",     icon: "💧" },
  fan_adjusted:     { label: "Fan",           bg: "bg-purple-100 dark:bg-purple-950", text: "text-purple-700 dark:text-purple-300", icon: "🌀" },
  harvested:        { label: "Harvested",     bg: "bg-emerald-100 dark:bg-emerald-950",text: "text-emerald-700 dark:text-emerald-300",icon: "🧺" },
  contam_spotted:   { label: "Contamination", bg: "bg-red-100 dark:bg-red-950",       text: "text-red-700 dark:text-red-400",       icon: "⚠️" },
  phase_transition: { label: "Phase",         bg: "bg-amber-100 dark:bg-amber-950",   text: "text-amber-700 dark:text-amber-300",   icon: "🔄" },
  photo_taken:      { label: "Photo",         bg: "bg-gray-100 dark:bg-gray-700",     text: "text-gray-600 dark:text-gray-300",     icon: "📷" },
  custom:           { label: "Event",         bg: "bg-gray-100 dark:bg-gray-700",     text: "text-gray-600 dark:text-gray-300",     icon: "📌" },
};

function EventBadge({ eventType }) {
  const style = EVENT_STYLES[eventType];
  if (!style) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
      {style.icon} {style.label}
    </span>
  );
}

function NoteItem({ note, growId, tz, photo = null }) {
  const updateNote = useUpdateNote(growId);
  const deleteNote = useDeleteNote(growId);
  const uploadPhoto = useUploadPhoto("grows", growId);
  const replacePhoto = useReplacePhoto("grows", growId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(note.note);
  const [editEventType, setEditEventType] = useState(note.event_type ?? "");
  const [editError, setEditError] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const photoInput = useRef(null);

  const photoBusy = uploadPhoto.isPending || replacePhoto.isPending;

  function startEdit() {
    setEditText(note.note);
    setEditEventType(note.event_type ?? "");
    setEditError(null);
    setConfirmDelete(false);
    setEditing(true);
  }

  async function attachPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setEditError(null);
    try {
      const prepared = await prepareForUpload(file);
      if (photo) {
        // Note already has a photo → replace it in place (keeps one photo per note).
        await replacePhoto.mutateAsync({ photoId: photo.id, file: prepared });
      } else {
        await uploadPhoto.mutateAsync({ file: prepared, noteId: note.id });
      }
    } catch (ex) {
      setEditError(ex.message || "Photo upload failed");
    }
  }

  async function saveEdit() {
    const trimmed = editText.trim();
    if (!trimmed) {
      setEditError("Note can't be empty");
      return;
    }
    setEditError(null);
    try {
      await updateNote.mutateAsync({
        noteId: note.id,
        note: trimmed,
        event_type: editEventType || null,
      });
      setEditing(false);
    } catch {
      setEditError("Failed to save changes");
    }
  }

  return (
    <div className="flex gap-3 group">
      <div className="w-1.5 shrink-0 mt-1.5 rounded-full self-stretch bg-gray-100 dark:bg-gray-700" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {note.event_type && <EventBadge eventType={note.event_type} />}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {formatDateTimeWithRelative(note.created_at, tz)}
            </span>
          </div>
          {!editing && !confirmDelete && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={startEdit}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-brand dark:hover:text-brand-onDark transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-danger dark:hover:text-danger-onDark transition-colors"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="mt-1">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={2}
              className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand resize-none"
            />
            <div className="flex items-center gap-2 mt-2">
              <select
                value={editEventType}
                onChange={(e) => setEditEventType(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <button
                onClick={saveEdit}
                disabled={updateNote.isPending || !editText.trim()}
                className="text-xs bg-brand dark:bg-brand-fillOnDark text-white rounded px-3 py-1.5 hover:bg-brand-hover dark:hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateNote.isPending ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                Cancel
              </button>
              {editError && <span className="text-xs text-danger dark:text-danger-onDark">{editError}</span>}
            </div>

            {/* Attach / replace a photo on an existing note */}
            <div className="mt-2 flex items-center gap-2">
              <input
                ref={photoInput}
                type="file"
                accept={isTouchDevice() ? "image/*" : "image/jpeg,image/png"}
                className="hidden"
                onChange={attachPhoto}
              />
              {photo?.thumbnail_url && (
                <img
                  src={photo.thumbnail_url}
                  alt={photo.caption || "Attached photo"}
                  className="h-10 w-10 rounded object-cover border border-gray-200 dark:border-gray-600"
                />
              )}
              <button
                type="button"
                onClick={() => photoInput.current?.click()}
                disabled={photoBusy}
                className="inline-flex items-center gap-1 rounded-md border border-sky-300 dark:border-sky-700 bg-white dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-brand dark:text-brand-onDark hover:bg-sky-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {photoBusy ? "Uploading…" : photo ? "📷 Replace photo" : "📷 Attach a photo"}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">{note.note}</p>
        )}

        {/* Attached photo thumbnail (view mode) — click to open full size */}
        {!editing && photo?.thumbnail_url && (
          <button
            type="button"
            onClick={() => setLightbox(true)}
            className="mt-2 block overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 hover:opacity-90"
            aria-label={photo.caption || "Open attached photo"}
          >
            <img
              src={photo.thumbnail_url}
              alt={photo.caption || "Attached photo"}
              className="h-20 w-20 object-cover"
              loading="lazy"
            />
          </button>
        )}

        {confirmDelete && !editing && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {photo ? "Delete this note and its photo? This can’t be undone." : "Delete this note? This can’t be undone."}
            </span>
            <button
              onClick={() => deleteNote.mutate(note.id)}
              disabled={deleteNote.isPending}
              className="text-xs text-danger dark:text-danger-onDark hover:text-danger-hover dark:hover:text-red-300 font-medium disabled:opacity-50"
            >
              {deleteNote.isPending ? "Deleting…" : "Yes, delete"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {lightbox && photo && <Lightbox photo={photo} onClose={() => setLightbox(false)} />}
    </div>
  );
}

export default function GrowNotesFeed({ growId, notes = [], photos = [] }) {
  const createNote = useCreateNote(growId);
  const uploadPhoto = useUploadPhoto("grows", growId);
  const tz = useTimezone();

  const [text, setText] = useState("");
  const [eventType, setEventType] = useState("");
  const [error, setError] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  // Map note id -> its attached photo (if any), so each note can show/attach a photo.
  const photoByNote = {};
  for (const p of photos) {
    if (p.note_id != null) photoByNote[p.note_id] = p;
  }

  const sorted = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const note = await createNote.mutateAsync({
        note: trimmed,
        ...(eventType ? { event_type: eventType } : {}),
      });
      // Opt-in link: when a photo is attached to a photo_taken note, upload it linked to
      // the note just created. A failed upload doesn't undo the note (it still exists).
      if (photoFile && note?.id != null) {
        try {
          const prepared = await prepareForUpload(photoFile);
          await uploadPhoto.mutateAsync({ file: prepared, noteId: note.id });
        } catch (ex) {
          setError(ex.message || "Note saved, but photo upload failed");
        }
      }
      setText("");
      setEventType("");
      setPhotoFile(null);
    } catch {
      setError("Failed to save note");
    }
  }

  return (
    <div className="bg-surface dark:bg-surface-dark rounded-xl shadow p-5">
      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Notes & Events</h2>

      {/* Add note form */}
      <form onSubmit={handleSubmit} className="mb-5">
        <textarea
          id="grow-note-text"
          name="note"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note…"
          rows={2}
          className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand resize-none"
        />
        <div className="flex items-center gap-2 mt-2">
          <select
            id="grow-note-event-type"
            name="eventType"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={createNote.isPending || uploadPhoto.isPending || !text.trim()}
            className="text-xs bg-brand dark:bg-brand-fillOnDark text-white rounded px-3 py-1.5 hover:bg-brand-hover dark:hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createNote.isPending || uploadPhoto.isPending ? "Saving…" : "Add note"}
          </button>
          {error && <span className="text-xs text-danger dark:text-danger-onDark">{error}</span>}
        </div>

        {/* Attach-photo row — shown when the Photo event is selected, so a note + its
            photo can be added in one step. */}
        {eventType === "photo_taken" && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-950 px-3 py-2">
            {!photoFile ? (
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white dark:bg-gray-700 border border-sky-300 dark:border-sky-700 px-3 py-1.5 text-xs font-medium text-brand dark:text-brand-onDark hover:bg-sky-100 dark:hover:bg-gray-600">
                <input
                  type="file"
                  accept={isTouchDevice() ? "image/*" : "image/jpeg,image/png"}
                  className="hidden"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                />
                📷 Attach a photo
              </label>
            ) : (
              <>
                <span className="inline-flex items-center gap-1 text-xs text-brand-dark dark:text-brand-onDark">
                  📷 <span className="max-w-[16rem] truncate">{photoFile.name}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setPhotoFile(null)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-danger dark:hover:text-danger-onDark"
                  aria-label="Remove attached photo"
                >
                  ✕ remove
                </button>
              </>
            )}
            <span className="ml-auto text-xs text-brand/80 dark:text-brand-onDark/80">
              Photo uploads with the note and appears on the timeline chart.
            </span>
          </div>
        )}
      </form>

      {/* Feed */}
      {sorted.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No notes yet. Add the first one above.</div>
      ) : (
        <div className="flex flex-col gap-4">
          {sorted.map((note) => (
            <NoteItem key={note.id} note={note} growId={growId} tz={tz} photo={photoByNote[note.id] ?? null} />
          ))}
        </div>
      )}
    </div>
  );
}
